import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { useValidateTransit } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { PrintItemSheetButton } from '@/routes/Requests/Request/components/RequestHeader/PrintOptions';

import { TrackingNumberInput } from './TrackingNumberInput';

export const ValidateTransitFromToNonDigitalizedWorkshopButton = ({
  onActionDone,
}: {
  onActionDone: () => void;
}) => {
  const { article, request } = useArticleContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isFromNonDigitalizedWorkshopToStore =
    !!article.atEntity?.workshopConfig?.nonDigitalized && article.toEntityId === request.storeId;

  const isFromStoreToNonDigitalizedWorkshop =
    !!article.toEntity?.workshopConfig?.nonDigitalized && article.atEntityId === request.storeId;

  if (!isFromNonDigitalizedWorkshopToStore && !isFromStoreToNonDigitalizedWorkshop) {
    return null;
  }

  const label = isFromStoreToNonDigitalizedWorkshop ? (
    <Trans id="article.actions.validate-transit">Validate transit</Trans>
  ) : (
    <Trans id="article.actions.processing.confirm">Confirm reception</Trans>
  );

  return (
    <>
      <Button onPress={() => setIsModalOpen(true)}>{label}</Button>
      <Dialog
        title={label}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        style={{ width: '32rem' }}
      >
        <ValidateTransitModalContent
          setIsOpen={setIsModalOpen}
          onActionDone={onActionDone}
          isFromNonDigitalizedWorkshopToStore={isFromNonDigitalizedWorkshopToStore}
        />
      </Dialog>
    </>
  );
};

const ValidateTransitModalContent = ({
  setIsOpen,
  onActionDone,
  isFromNonDigitalizedWorkshopToStore,
}: {
  setIsOpen: (isOpen: boolean) => void;
  onActionDone: () => void;
  isFromNonDigitalizedWorkshopToStore: boolean;
}) => {
  const { t } = useLingui();
  const { article } = useArticleContext();

  const [trackingNumber, setTrackingNumber] = useState<string | undefined>(undefined);
  const [apiError, setApiError] = useState<string | null>(null);

  const { mutateAsync: validateTransit, isPending: isValidateTransitPending } = useValidateTransit({
    articleId: article?.id,
  });

  const validate = async () => {
    setApiError(null);

    try {
      await validateTransit({ trackingNumber });
      setIsOpen(false);
      onActionDone();
    } catch (err: any) {
      setApiError(
        (err?.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <main>
        <Stack gap="1rem">
          <InlineText>
            {isFromNonDigitalizedWorkshopToStore ? (
              <Trans id="article.actions.processing.confirm.text">
                By confirming, you acknowledge the reception of the item.
              </Trans>
            ) : (
              <Trans id="article.validate-transit-modal.text">
                By validating the transit of this item, you confirm that you have handled the
                delivery yourself or arranged for someone (e.g., the recipient) to pick it up.
              </Trans>
            )}
          </InlineText>
          <TrackingNumberInput
            trackingNumber={trackingNumber}
            setTrackingNumber={setTrackingNumber}
          />
        </Stack>
      </main>
      <Footer>
        {!!apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <PrintItemSheetButton article={article} size="medium" variant="secondary" />
          <Button
            variant="primary"
            size="medium"
            isLoading={isValidateTransitPending}
            onPress={validate}
          >
            {isFromNonDigitalizedWorkshopToStore ? (
              <Trans id="article.actions.processing.confirm.button">Confirm</Trans>
            ) : (
              <Trans id="article.validate-transit-modal.actions.validate">Validate</Trans>
            )}
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
